import { useMemo, useState } from 'react'
import {
  ActionIcon,
  Avatar,
  Box,
  Button,
  Drawer,
  Indicator,
  ScrollArea,
  Text,
  Tooltip,
  UnstyledButton,
} from '@mantine/core'
import {
  IconAt,
  IconBell,
  IconBellOff,
  IconCalendarEvent,
  IconChecks,
  IconChevronRight,
  IconFile,
  IconFolder,
  IconInbox,
  IconMessageCircle,
  IconSquareCheck,
  IconUserCheck,
} from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import {
  fetchNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from '../api/notification'
import type { NotificationView } from '../api/types'
import { dayjs } from '../lib/dayjs'
import { ListSkeleton } from './Skeletons'
import { EmptyState } from './EmptyState'
import { EMPTY_COPY } from './empty-copy'
import {
  currentSubscription,
  disablePush,
  enablePush,
  isPushSupported,
  pushPermission,
} from '../push'
import classes from './NotificationBell.module.css'

/** Which notifications the drawer is showing. */
type Filter = 'all' | 'unread' | 'mentions'

/**
 * What is waiting for you, and a way to get to it.
 *
 * The bell carries the unread count and nothing else - a number visible from
 * across the screen. The list is a drawer rather than a dropdown, because
 * notifications are things you work through and a popover that closes when
 * you look away is the wrong shape for that.
 *
 * Kept current by the live stream, so the count changes as things happen
 * rather than when somebody happens to reload.
 */
export function NotificationBell() {
  const [open, setOpen] = useState(false)
  const [filter, setFilter] = useState<Filter>('all')
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const inbox = useQuery({
    queryKey: ['notifications'] as const,
    queryFn: ({ signal }) => fetchNotifications(30, signal),
  })

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['notifications'] })

  const read = useMutation({
    mutationFn: (id: string) => markNotificationRead(id, true),
    onSuccess: refresh,
  })

  const readAll = useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: refresh,
  })

  /*
    Whether this browser has agreed to be interrupted. Asked only when the
    drawer opens: checking the push manager on every page load wakes the
    service worker for a question nobody asked.
  */
  const push = useQuery({
    queryKey: ['push-subscription'] as const,
    queryFn: async () => ({
      supported: await isPushSupported(),
      permission: pushPermission(),
      subscribed: (await currentSubscription()) !== null,
    }),
    enabled: open,
    staleTime: 60_000,
  })

  const togglePush = useMutation({
    mutationFn: async (wanted: boolean) => {
      if (wanted) return enablePush()

      await disablePush()

      return false
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['push-subscription'] }),
  })

  const unread = inbox.data?.unread_count ?? 0
  const items = useMemo(() => inbox.data?.items ?? [], [inbox.data])
  const mentions = useMemo(() => items.filter(isMention).length, [items])

  const shown = useMemo(
    () =>
      items.filter((item) =>
        filter === 'unread' ? !item.read : filter === 'mentions' ? isMention(item) : true,
      ),
    [items, filter],
  )

  const days = useMemo(() => groupByDay(shown), [shown])

  const follow = (notification: NotificationView) => {
    if (!notification.read) read.mutate(notification.id)

    setOpen(false)

    // The server worked out where this points when it was written, so a
    // notification about something since renamed still lands in the right
    // place. Untyped because it comes from the API, not the route tree.
    if (notification.url) {
      void navigate({ to: notification.url as '/' })
    }
  }

  return (
    <>
      <Tooltip label="Notifications" openDelay={400}>
        <Indicator
          label={unread > 99 ? '99+' : unread}
          size={16}
          offset={5}
          disabled={unread === 0}
          color="red"
        >
          <ActionIcon
            variant="subtle"
            color="gray"
            size="lg"
            aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
            onClick={() => setOpen(true)}
          >
            <IconBell size={20} />
          </ActionIcon>
        </Indicator>
      </Tooltip>

      <Drawer
        opened={open}
        onClose={() => setOpen(false)}
        position="right"
        // 25% narrower than it was: the feed is one line of text per entry,
        // so the width was space around the sentences rather than more of
        // them, and it took a quarter of a laptop screen to say so.
        size={330}
        padding={0}
        overlayProps={{ backgroundOpacity: 0.3, blur: 2 }}
        classNames={{
          content: classes.content,
          header: classes.header,
          body: classes.body,
        }}
        title={
          <Text fw={800} size="19px" style={{ letterSpacing: '-0.03em' }}>
            Notifications
          </Text>
        }
      >
        <Box className={classes.tabs}>
          <FilterTab
            label="All"
            count={items.length}
            active={filter === 'all'}
            onClick={() => setFilter('all')}
          />
          <FilterTab
            label="Unread"
            count={unread}
            active={filter === 'unread'}
            onClick={() => setFilter('unread')}
          />
          <FilterTab
            label="Mentions"
            count={mentions}
            active={filter === 'mentions'}
            onClick={() => setFilter('mentions')}
          />
        </Box>

        {inbox.isPending ? (
          <Box p="md">
            <ListSkeleton rows={5} height={64} withAvatar />
          </Box>
        ) : shown.length === 0 ? (
          <Box style={{ flex: 1 }}>
            <EmptyState
              compact
              topic="tasks"
              icon={<IconInbox />}
              title={filter === 'all' ? EMPTY_COPY.inbox.title : EMPTY_COPY.inboxFiltered.title}
              hint={filter === 'all' ? EMPTY_COPY.inbox.hint : EMPTY_COPY.inboxFiltered.hint}
            />
          </Box>
        ) : (
          <ScrollArea type="auto" className={classes.scroller} style={{ flex: 1 }}>
            {days.map(({ label, notifications }) => (
              <Box key={label}>
                <Box className={classes.daySeparator}>{label}</Box>

                {notifications.map((item) => (
                  <NotificationRow key={item.id} notification={item} onClick={() => follow(item)} />
                ))}
              </Box>
            ))}
          </ScrollArea>
        )}

        {/*
          The two things you do to the list rather than to any one
          notification. Push is offered rather than asked for on load: a
          permission prompt that appears unbidden is the one everybody
          dismisses, and a dismissed prompt cannot be asked again.
        */}
        <Box className={classes.footer}>
          <Button
            size="compact-sm"
            variant="subtle"
            color="gray"
            leftSection={<IconChecks size={15} />}
            loading={readAll.isPending}
            disabled={unread === 0}
            onClick={() => readAll.mutate()}
          >
            Mark all as read
          </Button>

          {push.data?.supported && (
            <Tooltip
              label={
                push.data.permission === 'denied'
                  ? 'This browser is blocking notifications. Allow them in its site settings.'
                  : push.data.subscribed
                    ? 'You are notified on this device even when Medulla is closed'
                    : 'Be notified on this device even when Medulla is closed'
              }
              w={240}
              multiline
            >
              <Button
                size="compact-sm"
                variant="subtle"
                color={push.data.subscribed ? 'blue' : 'gray'}
                leftSection={
                  push.data.subscribed ? <IconBell size={15} /> : <IconBellOff size={15} />
                }
                loading={togglePush.isPending}
                disabled={push.data.permission === 'denied'}
                onClick={() => togglePush.mutate(!push.data.subscribed)}
              >
                {push.data.subscribed ? 'On this device' : 'Notify this device'}
              </Button>
            </Tooltip>
          )}
        </Box>
      </Drawer>
    </>
  )
}

function FilterTab({
  label,
  count,
  active,
  onClick,
}: {
  label: string
  count: number
  active: boolean
  onClick: () => void
}) {
  return (
    <UnstyledButton
      className={`${classes.tab}${active ? ` ${classes.tabActive}` : ''}`}
      onClick={onClick}
    >
      {label}
      {count > 0 && <span className={classes.tabCount}>{count}</span>}
    </UnstyledButton>
  )
}

/**
 * One notification.
 *
 * A face with a badge saying what kind of thing happened, the sentence the
 * server wrote, and the record itself in a box of its own underneath. The
 * record is separated out because it is the part somebody is going to click,
 * and because the title of a long task should not have to be read as the tail
 * of a sentence.
 */
function NotificationRow({
  notification,
  onClick,
}: {
  notification: NotificationView
  onClick: () => void
}) {
  const { icon: Icon, colour, context } = appearanceOf(notification)
  const { verb, subject } = split(notification)

  return (
    <UnstyledButton
      className={`${classes.row}${notification.read ? '' : ` ${classes.unread}`}`}
      onClick={onClick}
    >
      <Box className={classes.avatar}>
        <Avatar size={38} radius="xl" color="indigo">
          <Text size="13px" fw={700}>
            {initialsOf(notification.actor_name)}
          </Text>
        </Avatar>

        <Box className={classes.badge} style={{ background: `var(--mantine-color-${colour}-6)` }}>
          <Icon size={10} stroke={2.6} />
        </Box>
      </Box>

      <Box className={classes.main}>
        <Text className={classes.headline} lineClamp={2}>
          <span className={classes.actor}>{notification.actor_name}</span> {verb}
        </Text>

        {subject && (
          <Box className={classes.subject}>
            <Icon size={14} className={classes.subjectIcon} />

            <Text className={classes.subjectLabel} lineClamp={2}>
              {subject}
            </Text>

            <IconChevronRight size={14} className={classes.chevron} />
          </Box>
        )}
      </Box>

      <Box className={classes.meta}>
        <span className={classes.context}>{context}</span>
        <span className={classes.time}>{relativeTime(notification.created_at)}</span>
      </Box>
    </UnstyledButton>
  )
}

/** Up to two letters, from whatever the name turns out to be. */
function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)

  const first = parts[0]
  const last = parts[parts.length - 1]

  if (first === undefined || last === undefined) return '?'

  return (first.charAt(0) + (parts.length > 1 ? last.charAt(0) : '')).toUpperCase()
}

function isMention(notification: NotificationView): boolean {
  return notification.message.includes('mentioned you')
}

/**
 * "just now", "15 min ago", "4 hr ago", "3 d ago", then the date.
 *
 * The question the corner of a notification answers is "recent or not", and
 * the exact moment is on the thing itself.
 */
function relativeTime(iso: string): string {
  const moment = dayjs(iso)
  const minutes = dayjs().diff(moment, 'minute')

  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes} min ago`

  const hours = dayjs().diff(moment, 'hour')
  if (hours < 24) return `${hours} hr ago`

  const days = dayjs().diff(moment, 'day')
  if (days < 7) return `${days} d ago`

  return moment.format('D MMM')
}

/** The icon, the colour and the word for what kind of thing happened. */
function appearanceOf(notification: NotificationView): {
  icon: typeof IconBell
  colour: string
  context: string
} {
  if (isMention(notification)) {
    return { icon: IconAt, colour: 'grape', context: 'Mention' }
  }

  if (notification.action.includes('assigned')) {
    return { icon: IconUserCheck, colour: 'blue', context: 'Assigned' }
  }

  if (notification.subject_type.startsWith('comment.')) {
    return { icon: IconMessageCircle, colour: 'teal', context: 'Comment' }
  }

  if (notification.subject_type.startsWith('attachment.')) {
    return { icon: IconFile, colour: 'orange', context: 'File' }
  }

  if (notification.action.includes('due')) {
    return { icon: IconCalendarEvent, colour: 'red', context: 'Due' }
  }

  if (notification.subject_type.startsWith('project.task')) {
    return { icon: IconSquareCheck, colour: 'indigo', context: 'Task' }
  }

  if (notification.subject_type.startsWith('project.')) {
    return { icon: IconFolder, colour: 'violet', context: 'Project' }
  }

  return { icon: IconBell, colour: 'gray', context: 'Update' }
}

/**
 * What they did, and what they did it to.
 *
 * The server writes one readable sentence, which is what a push message and
 * any plain-text rendering need. The drawer has room to show the record
 * separately, so the actor's name comes off the front and the record's own
 * label comes off the end, leaving the verb phrase in the middle.
 *
 * The label is taken off only when the sentence genuinely ends with it - a
 * message the server phrased some other way is left whole rather than cut at
 * a guess.
 */
function split(notification: NotificationView): { verb: string; subject: string | null } {
  const { actor_name: actor, message, subject_label: label } = notification

  const rest = message.startsWith(actor) ? message.slice(actor.length).trim() : message

  if (label !== '' && rest.endsWith(label)) {
    const verb = rest.slice(0, rest.length - label.length).trim()

    return { verb: verb === '' ? rest : verb, subject: label }
  }

  return { verb: rest, subject: label === '' ? null : label }
}

/**
 * Today, Yesterday, then the date.
 *
 * A pile of thirty notifications with no breaks is a pile; the same thirty
 * under three headings is a history somebody can scan.
 */
function groupByDay(items: NotificationView[]): {
  label: string
  notifications: NotificationView[]
}[] {
  const groups = new Map<string, NotificationView[]>()

  for (const item of items) {
    const moment = dayjs(item.created_at)
    const label = moment.isSame(dayjs(), 'day')
      ? 'Today'
      : moment.isSame(dayjs().subtract(1, 'day'), 'day')
        ? 'Yesterday'
        : moment.format('D MMMM')

    groups.set(label, [...(groups.get(label) ?? []), item])
  }

  return [...groups.entries()].map(([label, notifications]) => ({ label, notifications }))
}
